import {memo, ReactNode, useMemo, useState} from 'react';
import {ValidationError} from '@api/lib/payrollProcessing/types';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import clsx from 'clsx';
import {Badge} from '@common-web/components/ui/badge';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import {AlertCircle, AlertTriangle, CheckCircle2, Copy, FileText} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {CollapseChevron} from '@common-web/components/collapse-chevron';
import {Button} from '@common-web/components/ui/button';

export type StructuralErrorConfig = {
  title: string;
  icon: () => ReactNode;
  getDescription: (error: ValidationError) => string;
  commonCause?: string;
  getAffectedFiles?: (error: ValidationError) => string[];
  getSteps: (error: ValidationError, onConfigureUnmapped: () => void) => ReactNode;
};

export const STRUCTURAL_ERROR_CONFIG: Record<string, StructuralErrorConfig> = {
  EMPTY_FILE: {
    title: 'Empty File Detected',
    icon: () => <FileText className='h-5 w-5' />,
    getDescription: () => 'One or more of your uploaded files contains no data rows.',
    commonCause:
      'The file may have only headers with no data, or the data format is not recognized.',
    getAffectedFiles: (error) => (error.fieldName ? [error.fieldName] : []),
    getSteps: () => (
      <StepsList
        steps={[
          'Open the file and verify it contains data rows below the header',
          'Ensure the data starts immediately after the header row',
          'Re-upload the file with the correct data',
        ]}
      />
    ),
  },
  NO_FILES_UPLOADED: {
    title: 'No Files Uploaded',
    icon: () => <FileText className='h-5 w-5' />,
    getDescription: () => 'No files were provided for processing.',
    commonCause: 'Files may not have been selected, or there was an issue during upload.',
    getSteps: () => (
      <StepsList
        steps={[
          'Go back to the upload step',
          'Select one or more payroll files to upload',
          'Ensure files are in CSV, XLSX, or XLS format',
        ]}
      />
    ),
  },
  INSUFFICIENT_COLUMNS: {
    title: 'Insufficient Columns',
    icon: () => <AlertCircle className='h-5 w-5' />,
    getDescription: () => 'A file must contain at least 2 columns of data to be processed.',
    commonCause: 'The file may be using the wrong delimiter, or the data structure is incorrect.',
    getAffectedFiles: (error) => (error.fieldName ? [error.fieldName] : []),
    getSteps: (error) => (
      <StepsList
        steps={[
          `Check the file "${error.fieldName || 'uploaded file'}" structure`,
          'Verify the file has proper column headers separated correctly',
          'Ensure your data has multiple columns (e.g., name, ID, wages)',
          'Re-upload a corrected version of the file',
        ]}
      />
    ),
  },
  DUPLICATE_COLUMN_NAMES: {
    title: 'Duplicate Column Names Detected',
    icon: () => <Copy className='h-5 w-5' />,
    getDescription: () =>
      'Your file contains multiple columns with the same name. When this happens, only the rightmost (last) column with that name is used for processing.',
    commonCause:
      'Column headers may have been accidentally duplicated, or the file was merged from multiple sources without renaming columns.',
    getAffectedFiles: (error) => (error.fieldName ? [error.fieldName] : []),
    getSteps: (error) => {
      const duplicates = error.rowContext
        ? Object.entries(error.rowContext).map(([name, info]) => ({name, info}))
        : [];
      return (
        <div className='space-y-4'>
          {duplicates.length > 0 && (
            <div className='bg-white rounded-lg p-3 border border-gray-200'>
              <div className='text-xs font-semibold text-gray-500 uppercase mb-2'>
                Duplicate Columns
              </div>
              <div className='flex flex-wrap gap-2'>
                {duplicates.map(({name, info}, idx) => (
                  <div
                    key={idx}
                    className='flex items-center gap-1.5 border border-gray-200 rounded-sm px-2 py-1'
                  >
                    <code className='font-mono text-xs text-gray-800'>{name}</code>
                    <span className='text-xs text-gray-500'>({info})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
            <div className='flex items-start gap-2'>
              <CheckCircle2 className='h-4 w-4 text-blue-600 mt-0.5 shrink-0' />
              <p className='text-sm text-blue-800'>
                Processing will continue using the rightmost column for each duplicate name. If this
                is intentional, you can proceed without changes.
              </p>
            </div>
          </div>
          <StepsList
            steps={[
              'Open the source file and check for duplicate column headers',
              'Rename duplicate columns to have unique names if needed',
              'Re-upload the corrected file',
            ]}
          />
        </div>
      );
    },
  },
  MISSING_FILE_FOR_MAPPING: {
    title: 'Missing File for Mapping',
    icon: () => <FileText className='h-5 w-5' />,
    getDescription: () => 'A file referenced in your column mappings was not uploaded.',
    commonCause:
      'Your saved configuration references a file that was uploaded previously but not included this time.',
    getAffectedFiles: (error) => (error.fieldName ? [error.fieldName] : []),
    getSteps: (error) => (
      <StepsList
        steps={[
          `Upload the missing file: "${error.fieldName}"`,
          'Or update your column mappings to only use columns from your current uploaded files',
        ]}
      />
    ),
  },
  MISSING_COLUMNS_IN_FILE: {
    title: 'Missing Columns in File',
    icon: () => <AlertCircle className='h-5 w-5' />,
    getDescription: () =>
      'Some columns expected based on your configuration were not found in the uploaded files.',
    commonCause:
      'Column names may have changed, been renamed, or the file structure is different from the configured mapping.',
    getAffectedFiles: (error) => {
      if (!error.rowContext) return [];
      return [...new Set(Object.values(error.rowContext))];
    },
    getSteps: (error, onConfigureUnmapped) => {
      const missingColumns = error.rowContext
        ? Object.entries(error.rowContext).map(([col, fileId]) => ({col, fileId}))
        : [];
      return (
        <div className='space-y-4'>
          {missingColumns.length > 0 && (
            <div className='bg-white rounded-lg p-3 border border-gray-200'>
              <div className='text-xs font-semibold text-gray-500 uppercase mb-2'>
                Missing Columns ({missingColumns.length})
              </div>
              <div className='flex flex-wrap gap-2'>
                {missingColumns.map(({col, fileId}, idx) => (
                  <div
                    key={idx}
                    className='flex items-center gap-1.5 border border-gray-200 rounded-sm px-2 py-1'
                  >
                    <FileIdBadge fileId={fileId} />
                    <code className='font-mono text-xs text-gray-800'>{col}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
          <StepsList
            steps={[
              'Check that your uploaded files contain the expected columns',
              'Verify column names match exactly (including capitalization and spacing)',
              'Update your column mappings if column names have changed',
            ]}
          />
          <Button
            variant='default'
            className={'mt-2 bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'}
            size='sm'
            onClick={onConfigureUnmapped}
          >
            Update Column Mappings
          </Button>
        </div>
      );
    },
  },
  REQUIRED_FIELDS_NOT_MAPPED: {
    title: 'Required Fields Not Mapped',
    icon: () => <AlertCircle className='h-5 w-5' />,
    getDescription: () => 'Some required fields are not mapped to any column in your files.',
    commonCause:
      'The initial configuration may be incomplete, or required fields were removed from mappings.',
    getSteps: (error, onConfigureUnmapped) => (
      <div className='space-y-4'>
        <StepsList
          steps={[
            'Go to the column mapping configuration',
            'Find and map all required fields (marked with a red asterisk)',
            'Assign a source column from your files to each required field',
          ]}
        />
        <Button variant='outline' size='sm' onClick={onConfigureUnmapped}>
          Configure Column Mappings
        </Button>
      </div>
    ),
  },
  UNMAPPED_COLUMNS_DETECTED: {
    title: 'Unmapped Columns Detected',
    icon: () => <AlertTriangle className='h-5 w-5' />,
    getDescription: () =>
      'Some columns in your files are not mapped to any field. All columns must either be mapped or explicitly ignored.',
    commonCause:
      'New columns were added to your files, or some columns were never configured during initial setup.',
    getAffectedFiles: (error) => {
      if (!error.rowContext) return [];
      return [...new Set(Object.values(error.rowContext))];
    },
    getSteps: (error, onConfigureUnmapped) => {
      const unmappedColumns = error.rowContext
        ? Object.entries(error.rowContext).map(([col, fileId]) => ({col, fileId}))
        : [];
      return (
        <div className='space-y-4'>
          {unmappedColumns.length > 0 && (
            <div className='bg-white rounded-lg p-3 border border-gray-200'>
              <div className='text-xs font-semibold text-gray-500 uppercase mb-2'>
                Unmapped Columns ({unmappedColumns.length})
              </div>
              <div className='flex flex-wrap gap-2'>
                {unmappedColumns.map(({col, fileId}, idx) => (
                  <div
                    key={idx}
                    className='flex items-center gap-1.5 border border-gray-200 rounded-sm px-2 py-1'
                  >
                    <FileIdBadge fileId={fileId} />
                    <code className='font-mono text-xs text-gray-800'>{col}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
          <StepsList
            steps={[
              'Review the unmapped columns listed above',
              'Map columns to target fields if they contain useful data',
              'Mark columns as "Ignored" if they are not needed for processing',
            ]}
          />
          <Button
            variant='default'
            className={'bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'}
            size='sm'
            onClick={onConfigureUnmapped}
          >
            Configure Columns
          </Button>
        </div>
      );
    },
  },
  MISSING_FALLBACK_COLUMNS_IN_FILE: {
    title: 'Missing Fallback Columns in File',
    icon: () => <AlertCircle className='h-5 w-5' />,
    getDescription: () =>
      'Some fallback columns configured in your mappings were not found in the uploaded files.',
    commonCause:
      'Fallback column names may have changed, been renamed, or the file structure is different from when the fallbacks were configured.',
    getAffectedFiles: (error) => {
      if (!error.rowContext) return [];
      return [...new Set(Object.values(error.rowContext))];
    },
    getSteps: (error, onConfigureUnmapped) => {
      const missingColumns = error.rowContext
        ? Object.entries(error.rowContext).map(([col, fileId]) => ({col, fileId}))
        : [];
      return (
        <div className='space-y-4'>
          {missingColumns.length > 0 && (
            <div className='bg-white rounded-lg p-3 border border-gray-200'>
              <div className='text-xs font-semibold text-gray-500 uppercase mb-2'>
                Missing Fallback Columns ({missingColumns.length})
              </div>
              <div className='flex flex-wrap gap-2'>
                {missingColumns.map(({col, fileId}, idx) => (
                  <div
                    key={idx}
                    className='flex items-center gap-1.5 border border-gray-200 rounded-sm px-2 py-1'
                  >
                    <FileIdBadge fileId={fileId} />
                    <code className='font-mono text-xs text-gray-800'>{col}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
          <StepsList
            steps={[
              'Check that your uploaded files contain the expected fallback columns',
              'Verify fallback column names match exactly (including capitalization and spacing)',
              'Update your column mappings to remove or fix invalid fallback column references',
            ]}
          />
          <Button
            variant='default'
            className={'bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white mt-2'}
            size='sm'
            onClick={onConfigureUnmapped}
          >
            Update Column Mappings
          </Button>
        </div>
      );
    },
  },
  UNLINKED_ROWS_IGNORED: {
    title: 'Unlinked Rows Skipped',
    icon: () => <AlertTriangle className='h-5 w-5' />,
    getDescription: (error) => error.message,
    commonCause:
      "Some records in supplemental files don't have matching entries in the primary file based on the configured link columns.",
    getAffectedFiles: (error) => (error.fieldName ? [error.fieldName] : []),
    getSteps: () => (
      <div className='space-y-4'>
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
          <div className='flex items-start gap-2'>
            <CheckCircle2 className='h-4 w-4 text-blue-600 mt-0.5 shrink-0' />
            <p className='text-sm text-blue-800'>
              This is often expected when supplemental files contain extra records that don't have
              corresponding entries in the primary file.
            </p>
          </div>
        </div>
        <StepsList
          steps={[
            'Verify this is expected behavior for your data',
            'If unexpected, check that your file link columns match correctly between files',
            'Ensure employee IDs or other linking fields are consistent across all files',
          ]}
        />
      </div>
    ),
  },
  ANCHORS_REQUIRED: {
    title: 'File Links Required',
    icon: () => <AlertCircle className='h-5 w-5' />,
    getDescription: () =>
      'When uploading multiple files, you must configure at least one file link to connect the data.',
    commonCause:
      'Multiple files were uploaded without configuring how they should be joined together.',
    getSteps: (error, onConfigureUnmapped) => (
      <div className='space-y-4'>
        <StepsList
          steps={[
            'Go to the column mapping configuration',
            'Set up file links using a common field (e.g., Employee ID, SSN)',
            'Select the matching column from each file that contains the same identifier',
          ]}
        />
        <Button variant='outline' size='sm' onClick={onConfigureUnmapped}>
          Configure File Links
        </Button>
      </div>
    ),
  },
  ANCHOR_FILE_MISSING: {
    title: 'Anchor File Missing',
    icon: () => <FileText className='h-5 w-5' />,
    getDescription: () => 'A file referenced in your file link configuration was not uploaded.',
    commonCause:
      'Your saved configuration references a file that was uploaded previously but not included this time.',
    getAffectedFiles: (error) => (error.fieldName ? [error.fieldName] : []),
    getSteps: (error) => (
      <StepsList
        steps={[
          `Upload the missing file: "${error.fieldName}"`,
          'Or update your file link configuration to use only uploaded files',
        ]}
      />
    ),
  },
  ANCHOR_MISSING_IN_FILE: {
    title: 'File Link Not Configured',
    icon: () => <AlertCircle className='h-5 w-5' />,
    getDescription: () =>
      'This file does not have any columns configured for linking with other files.',
    commonCause:
      'When using multiple files, each file needs a column designated for matching records across files.',
    getAffectedFiles: (error) => (error.fieldName ? [error.fieldName] : []),
    getSteps: (error, onConfigureUnmapped) => (
      <div className='space-y-4'>
        <StepsList
          steps={[
            `Configure a linking column for the file "${error.fieldName}"`,
            'Choose a column that contains unique identifiers (e.g., Employee ID)',
            'Ensure the values in this column match across your files',
          ]}
        />
        <Button variant='outline' size='sm' onClick={onConfigureUnmapped}>
          Configure File Links
        </Button>
      </div>
    ),
  },
  FILE_PARSE_ERROR: {
    title: 'File Parse Error',
    icon: () => <AlertCircle className='h-5 w-5' />,
    getDescription: (error) => error.message,
    commonCause: 'The file may be corrupted, password protected, or in an unsupported format.',
    getSteps: () => (
      <StepsList
        steps={[
          'Ensure your file is a valid CSV, XLSX, or XLS format',
          `Try opening the file in Excel to verify it's not corrupted`,
          `Check that the file isn't password protected`,
          'Re-export and upload a fresh copy of the file',
        ]}
      />
    ),
  },
};

export const DEFAULT_ERROR_CONFIG: StructuralErrorConfig = {
  title: 'File Structure Issue',
  icon: () => <AlertCircle className='h-5 w-5' />,
  getDescription: (error) => error.message,
  commonCause: 'There may be an issue with your file format or configuration.',
  getSteps: () => (
    <StepsList
      steps={[
        'Review the error message above for specific details',
        'Check your file format and structure',
        'Update your configuration or re-upload corrected files',
      ]}
    />
  ),
};

function StepsList({steps, className}: {steps: string[]; className?: string}) {
  return (
    <div className={clsx('space-y-2', className)}>
      {steps.map((step, idx) => (
        <div key={idx} className='flex items-start gap-3'>
          <span className='shrink-0 w-6 h-6 rounded-full bg-internal-brand-blue text-white text-xs font-semibold flex items-center justify-center'>
            {idx + 1}
          </span>
          <span className='text-sm text-gray-700 pt-0.5'>{step}</span>
        </div>
      ))}
    </div>
  );
}

export const StructuralErrorCard = memo(
  ({error, onConfigureUnmapped}: {error: ValidationError; onConfigureUnmapped: () => void}) => {
    const config = STRUCTURAL_ERROR_CONFIG[error.type] || DEFAULT_ERROR_CONFIG;
    const isError = error.level === 'HIGH';
    const [stepsOpen, setStepsOpen] = useState(isError);

    const affectedFiles = useMemo(() => {
      if (config.getAffectedFiles) {
        return config.getAffectedFiles(error);
      }
      return [];
    }, [config, error]);

    return (
      <Card
        className={clsx(
          'shadow-sm hover:shadow-md transition-shadow overflow-hidden',
          isError && 'border-red-300',
        )}
      >
        <CardHeader
          className={clsx(
            'pb-3',
            isError ? 'bg-red-50 border-b border-red-200' : 'border-b border-gray-200',
          )}
        >
          <div className='flex items-start gap-3'>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2 flex-wrap'>
                <Badge
                  className={clsx(
                    'text-xs font-semibold border-0',
                    isError
                      ? 'bg-red-600 hover:bg-red-600 text-white'
                      : 'bg-amber hover:bg-amber text-white',
                  )}
                >
                  <span>{isError ? 'BLOCKS PROCESSING' : 'WARNING'}</span>
                </Badge>
                <CardTitle
                  className={clsx(
                    'text-lg font-semibold',
                    isError ? 'text-red-800' : 'text-gray-800',
                  )}
                >
                  {config.title}
                </CardTitle>
              </div>
              <p className={clsx('text-sm mt-2', isError ? 'text-red-700' : 'text-gray-800')}>
                {config.getDescription(error)}
              </p>
              {affectedFiles.length > 0 && (
                <div className='flex items-center gap-2 mt-2 flex-wrap'>
                  <span className='text-xs text-gray-500'>Affected:</span>
                  {affectedFiles.map((fileId, idx) => (
                    <FileIdBadge key={idx} fileId={fileId} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className='pt-4 space-y-4'>
          {!!config.commonCause && (
            <div className='bg-gray-50 border border-gray-200 rounded-lg p-3'>
              <div className='flex items-center gap-2'>
                <AlertCircle className='h-4 w-4 text-gray-400 shrink-0' />
                <div className='text-xs font-semibold text-gray-500 uppercase'>Common Cause</div>
              </div>
              <p className='text-sm text-gray-800 mt-2'>{config.commonCause}</p>
            </div>
          )}
          <Collapsible open={stepsOpen} onOpenChange={setStepsOpen}>
            <CollapsibleTrigger asChild>
              <button
                className={clsx(
                  'flex items-center gap-2 text-xs font-semibold uppercase hover:opacity-80 transition-opacity',
                  isError && 'text-red-600',
                )}
              >
                <CollapseChevron open={stepsOpen} className='h-4 w-4' />
                How to fix
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className='pt-3'>
              {config.getSteps(error, onConfigureUnmapped)}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>
    );
  },
);
